import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Stethoscope, Plus, UserMinus, Loader2, Mail, Shield, UserCheck, Clock, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from '../../../hooks/use-toast';
import api from '../../../utils/api';

const DoctorManagement = ({ clinicId }) => {
  const { t } = useTranslation();
  const [affiliations, setAffiliations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('consultant');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clinicId) return;
    fetchAffiliations();
  }, [clinicId]);

  const fetchAffiliations = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/providers/${clinicId}/doctors`);
      if (data.success) setAffiliations(data.affiliations || []);
    } catch (err) {
      console.error('[DoctorManagement] fetch affiliations error:', err);
    } finally { setLoading(false); }
  };

  const handleInviteDoctor = async () => {
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    try {
      const { data } = await api.post(`/providers/${clinicId}/doctors/invite`,
        { doctor_email: inviteEmail.trim(), role: inviteRole, visibility: 'public' });
      if (data.success) {
        setAffiliations(prev => [data.affiliation, ...prev]);
        setInviteEmail('');
        toast({ title: t('provider.team.doctors.invitationSentTitle'), description: t('provider.team.doctors.invitationSentBody', { email: inviteEmail }) });
      }
    } catch (err) {
      toast({
        title: t('provider.team.doctors.invitationFailedTitle'),
        description: err?.response?.data?.detail || err?.response?.data?.message || t('provider.team.doctors.invitationFailedBody'),
        variant: 'destructive'
      });
    } finally { setInviteLoading(false); }
  };

  const handleRevokeAffiliation = async (affId) => {
    try {
      await api.delete(`/providers/${clinicId}/doctors/${affId}`);
      setAffiliations(prev => prev.map(a => a.affiliation_id === affId ? { ...a, status: 'revoked' } : a));
      toast({ title: t('provider.team.doctors.revokedTitle'), description: t('provider.team.doctors.revokedBody') });
    } catch (err) {
      toast({ title: t('provider.team.doctors.errorTitle'), description: t('provider.team.doctors.revokeFailed'), variant: 'destructive' });
    }
  };

  const activeAffs = affiliations.filter(a => a.status === 'active');
  const pendingAffs = affiliations.filter(a => a.status === 'pending');
  const revokedAffs = affiliations.filter(a => a.status === 'revoked' || a.status === 'rejected');

  const statusConfig = {
    active: { bg: 'bg-emerald-50 border-emerald-200', badge: 'border-emerald-300 text-emerald-700', icon: UserCheck, labelKey: 'provider.team.statuses.active' },
    pending: { bg: 'bg-amber-50 border-amber-200', badge: 'border-amber-300 text-amber-700', icon: Clock, labelKey: 'provider.team.statuses.pending' },
    revoked: { bg: 'bg-slate-50 border-slate-200', badge: 'border-red-300 text-red-600', icon: XCircle, labelKey: 'provider.team.statuses.revoked' },
    rejected: { bg: 'bg-slate-50 border-slate-200', badge: 'border-red-300 text-red-600', icon: XCircle, labelKey: 'provider.team.statuses.rejected' },
  };

  return (
    <div className="space-y-6" data-testid="doctor-management">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="doctor-mgmt-title">{t('provider.team.doctors.title')}</h2>
          <p className="text-gray-500 text-sm">{t('provider.team.doctors.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAffiliations} data-testid="refresh-affiliations-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> {t('provider.team.doctors.refresh')}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-testid="ssot-info-banner">
        <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-medium text-blue-900 text-sm">{t('provider.team.doctors.ssotBannerTitle')}</p>
          <p className="text-xs text-blue-700">
            {t('provider.team.doctors.ssotBannerBody')} <span className="font-mono bg-blue-100 px-1 rounded">/#/doctor/signup</span>{t('provider.team.doctors.ssotBannerSuffix')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4" data-testid="affiliation-stats">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600" data-testid="active-count">{activeAffs.length}</p>
          <p className="text-xs text-gray-500">{t('provider.team.doctors.activeAffiliations')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600" data-testid="pending-count">{pendingAffs.length}</p>
          <p className="text-xs text-gray-500">{t('provider.team.doctors.pendingInvitations')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-slate-500" data-testid="revoked-count">{revokedAffs.length}</p>
          <p className="text-xs text-gray-500">{t('provider.team.doctors.revokedRejected')}</p>
        </CardContent></Card>
      </div>

      <Card data-testid="invite-doctor-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" /> {t('provider.team.doctors.inviteTitle')}
          </CardTitle>
          <CardDescription className="text-xs">{t('provider.team.doctors.inviteDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2" data-testid="invite-doctor-form">
            <Input
              placeholder={t('provider.team.doctors.inviteEmailPlaceholder')}
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              className="flex-1"
              data-testid="invite-email-input"
              onKeyDown={e => e.key === 'Enter' && handleInviteDoctor()}
            />
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="border rounded-md px-3 text-sm" data-testid="invite-role-select">
              <option value="consultant">{t('provider.team.roles.consultant')}</option>
              <option value="surgeon">{t('provider.team.roles.surgeon')}</option>
              <option value="advisor">{t('provider.team.roles.advisor')}</option>
            </select>
            <Button onClick={handleInviteDoctor} disabled={inviteLoading || !inviteEmail.trim()} data-testid="invite-submit-btn">
              {inviteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              {t('provider.team.doctors.inviteBtn')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="affiliations-list-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Stethoscope className="h-4 w-4" /> {t('provider.team.doctors.allAffiliations', { count: affiliations.length })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8" data-testid="affiliations-loading">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
              <span className="text-sm text-slate-400">{t('provider.team.doctors.loadingAffiliations')}</span>
            </div>
          ) : affiliations.length === 0 ? (
            <div className="text-center py-8" data-testid="no-affiliations">
              <Stethoscope className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">{t('provider.team.doctors.noAffiliationsTitle')}</p>
              <p className="text-xs text-slate-400 mt-1">{t('provider.team.doctors.noAffiliationsBody')}</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="affiliations-list">
              {affiliations.map(aff => {
                const sc = statusConfig[aff.status] || statusConfig.revoked;
                const StatusIcon = sc.icon;
                const dp = aff.doctor_profile;
                return (
                  <div key={aff.affiliation_id} className={`flex items-center justify-between rounded-lg p-3 text-sm border ${sc.bg}`} data-testid={`aff-${aff.affiliation_id}`}>
                    <div className="flex items-center gap-3">
                      <StatusIcon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <div>
                        <span className="font-medium" data-testid={`aff-name-${aff.affiliation_id}`}>
                          {dp?.full_name || aff.doctor_name || aff.doctor_email || t('provider.team.doctors.unknownName')}
                        </span>
                        {(dp?.email || aff.doctor_email) && (dp?.full_name || aff.doctor_name) && (
                          <span className="text-xs text-slate-400 ml-1">({dp?.email || aff.doctor_email})</span>
                        )}
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <Badge variant="outline" className="text-[10px]">{aff.role}</Badge>
                          {dp?.verification_status && (
                            <Badge variant="outline" className={`text-[10px] ${
                              dp.verification_status === 'verified' ? 'border-emerald-300 text-emerald-600' :
                              dp.verification_status === 'pending' ? 'border-amber-300 text-amber-600' :
                              'border-slate-300 text-slate-500'
                            }`} data-testid={`aff-verification-${aff.affiliation_id}`}>
                              {dp.verification_status}
                            </Badge>
                          )}
                          {dp?.specialties?.length > 0 && (
                            <span className="text-[10px] text-slate-400" data-testid={`aff-specialties-${aff.affiliation_id}`}>
                              {dp.specialties.slice(0, 3).map(s => s.replace(/_/g, ' ')).join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${sc.badge}`}>{t(sc.labelKey)}</Badge>
                      {['active', 'pending'].includes(aff.status) && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleRevokeAffiliation(aff.affiliation_id)} data-testid={`revoke-${aff.affiliation_id}`}>
                          <UserMinus className="h-3 w-3 mr-1" /> {t('provider.team.doctors.revoke')}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="lifecycle-info">
        <CardContent className="p-4">
          <h4 className="text-xs font-semibold text-slate-600 mb-2">{t('provider.team.doctors.lifecycleTitle')}</h4>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400"></div>
              <span>{t('provider.team.doctors.lifecyclePending')}</span>
            </div>
            <span className="text-slate-300">&rarr;</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              <span>{t('provider.team.doctors.lifecycleActive')}</span>
            </div>
            <span className="text-slate-300">&rarr;</span>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-400"></div>
              <span>{t('provider.team.doctors.lifecycleRevoked')}</span>
            </div>
            <span className="text-slate-300">|</span>
            <span className="text-slate-400 italic">{t('provider.team.doctors.lifecycleReactivate')}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DoctorManagement;
